import { Discount } from "./discount"
import { ReceptionEvaluatedPart } from "./evaluated-service-bike-part"
import { PriceUtils } from "./price-utils"
import { ServicePackage } from "./service-package"
import {
  BikeCharacteristics,
  ServicePackagePriceResolver,
} from "./service-package-price-resolver"
import { ServicePackageUtils } from "./service-package-utils"
import { ServiceAction } from "./service-part-action"
import { ServiceWash } from "./service-wash"

export interface PackageCostCalculationInput {
  selectedPackageIds: string[]
  evaluatedParts: ReceptionEvaluatedPart[]
  selectedWashId: string | null
  availablePackages: ServicePackage[]
  availableWashes: ServiceWash[]
  packageDiscounts: Record<string, Discount | null> | undefined
  bikeCharacteristics: BikeCharacteristics
}

export interface PackageCostResult {
  packagesCost: number
  individualPartsCost: number
  washCost: number
  totalCost: number
}

/**
 * Calculates the total cost for a given set of packages, parts, and wash.
 * Takes into account:
 * - Package prices (resolved for bike characteristics)
 * - Package discounts
 * - Individual part costs (for parts not covered by packages)
 * - Wash cost (if not included in a package)
 */
export function calculatePackageCost(
  input: PackageCostCalculationInput,
): PackageCostResult {
  const {
    selectedPackageIds,
    evaluatedParts,
    selectedWashId,
    availablePackages,
    availableWashes,
    packageDiscounts,
    bikeCharacteristics,
  } = input

  // Calculate packages cost
  let packagesCost = 0
  for (const pkgId of selectedPackageIds) {
    const pkg = availablePackages.find((p) => p.id === pkgId)
    if (pkg) {
      const priceResult = ServicePackagePriceResolver.resolvePrice(
        pkg,
        bikeCharacteristics,
      )
      if (priceResult) {
        const discount = packageDiscounts?.[pkgId] ?? null
        packagesCost += PriceUtils.applyDiscount(priceResult.price, discount)
      }
    }
  }

  // Get all included parts with their actions from selected packages
  const allIncludedPartsFromPackages: {
    partId: string
    includedActions: ServiceAction[]
  }[] = []
  for (const pkgId of selectedPackageIds) {
    const pkg = availablePackages.find((p) => p.id === pkgId)
    if (pkg) {
      for (const item of pkg.items) {
        allIncludedPartsFromPackages.push({
          partId: item.partId,
          includedActions: item.includedActions,
        })
      }
    }
  }

  // Calculate individual parts cost (not covered by packages)
  // A part is only covered if both the partId AND its recommendedAction
  // are included in a package's items
  let individualPartsCost = 0
  for (const part of evaluatedParts) {
    const isCoveredByPackage = ServicePackageUtils.partActionIsPartOfPackage(
      { partId: part.id, action: part.recommendedAction },
      allIncludedPartsFromPackages,
    )
    if (!isCoveredByPackage) {
      individualPartsCost += PriceUtils.applyDiscount(
        part.laborPrice,
        part.laborPriceDiscount ?? null,
      )
    }
  }

  // Calculate wash cost (if not in a package)
  let washCost = 0
  if (selectedWashId) {
    const washInPackage = selectedPackageIds.some((pkgId) => {
      const pkg = availablePackages.find((p) => p.id === pkgId)
      return pkg?.wash?.id === selectedWashId
    })
    if (!washInPackage) {
      const wash = availableWashes.find((w) => w.id === selectedWashId)
      washCost = wash?.price ?? 0
    }
  }

  return {
    packagesCost,
    individualPartsCost,
    washCost,
    totalCost: packagesCost + individualPartsCost + washCost,
  }
}

/**
 * Compares two sets and returns true if they contain the same elements.
 */
export function areSetsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false
  for (const item of a) {
    if (!b.has(item)) return false
  }
  return true
}

/**
 * Determines if a new package combination should be applied.
 * Returns true if the new combination is strictly cheaper.
 */
export function shouldApplyOptimization(
  currentCost: number,
  newCost: number,
): boolean {
  return newCost < currentCost
}
